import React, {useContext, useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Link, useParams} from 'react-router-dom';

import {UserLink} from 'src/components/links';
import {ServerResponseHandler} from 'src/components/server-response-handler';
import {mapDateFormat} from 'src/utils/mapDateFormat';

import {BoxLoading} from '@pb/kit-component-box-loading';
import {FormSection} from '@pb/kit-component-form-section';
import {FormattedDate} from '@pb/kit-component-formatters';
import {Group} from '@pb/kit-component-group';
import {Body, Cell, Head, Row, Table} from '@pb/kit-component-table';
import {LocaleContext} from '@pb/kit-context-locale';


import {useGetProfileHistory} from './hooks/useGetProfileHistory';
import {ProfileHistoryPagination} from './pagination';
import {profileHistoryStateActions, profileHistoryStateSelector} from './slices/profile-history-state.slice';
import {profileHistoryActions} from './slices/profile-history.slice';

import type {ProfileChangeRecord} from 'src/openapi/admin/models/ProfileChangeRecord';

export const ProfileHistoryWrapper = () => {
    const queryParams = useParams<{roleId: string}>();
    const dispatch = useDispatch();
    const {searchCriteria} = useSelector(profileHistoryStateSelector);

    useEffect(() => {
        if (queryParams.roleId) {
            dispatch(profileHistoryStateActions.setSearchCriteria({profileId: +queryParams.roleId}));
        }
    }, [queryParams, dispatch]);

    if (!searchCriteria.profileId) {
        return null;
    }

    return <ProfileHistoryTable profileId={+searchCriteria.profileId}/>;
};

type Props = {
    profileId: number,
};

const ProfileHistoryTable = ({profileId}: Props) => {
    const dispatch = useDispatch();
    const {result, loadingError} = useGetProfileHistory();
    const {locale} = useContext(LocaleContext);

    useEffect(() => {
        return () => {
            dispatch(profileHistoryActions.clearData());
            dispatch(profileHistoryStateActions.clearData());
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <ServerResponseHandler serverError={loadingError} smallView={true}>
            <BoxLoading loading={!result}>
                <FormSection headline="Audit Log">
                    <Group direction="v">
                        <Table borderedGrid={true}>
                            <Head>
                                <Cell>Change Type</Cell>
                                <Cell>Changed By</Cell>
                                <Cell>Change Date</Cell>
                                <Cell>Notes</Cell>
                            </Head>
                            <Body>
                                {result?.data.map((record: ProfileChangeRecord) => (
                                    <Row key={record.eventId}>
                                        <Cell>
                                            <Link to={`/roles/${profileId}/history/${record.eventId}`}>
                                                {record.operationDescription}
                                            </Link>
                                        </Cell>
                                        <Cell>
                                            <UserLink
                                                userId={record.editor.userId}
                                                caption={[
                                                    record.editor.firstName,
                                                    record.editor.lastName,
                                                ].join(' ') || record.editor.email}
                                                place="Role Profile History"
                                            />
                                        </Cell>
                                        <Cell>
                                            {FormattedDate.format({
                                                value: record.eventTime,
                                                format: mapDateFormat(locale.dateFormat, 'NUMERIC', 'DATE_TIME'),
                                            })}
                                        </Cell>
                                        <Cell>
                                            {record.notes}
                                        </Cell>
                                    </Row>
                                ))}
                            </Body>
                        </Table>
                        <ProfileHistoryPagination totalCount={result?.totalCount || 0}/>
                    </Group>
                </FormSection>
            </BoxLoading>
        </ServerResponseHandler>
    );
};
